import { Controller } from "@hotwired/stimulus"
import { csrfToken } from "../utils/csrf"

// Drag-to-move for the applicant pipeline board. Reuses the app's existing
// dependency-free HTML5 drag pattern (the same dragstart / dragover / drop
// wiring as tasks_outline_controller) rather than pulling in a sortable lib.
//
// A card carries data-application-id and its column carries data-stage-id.
// Dropping a card on a different column optimistically re-parents it in the
// DOM, then PATCHes the move to the server (recording an ApplicationStageEvent
// server-side). On failure the card snaps back to where it came from.
//
// values: moveUrlTemplate — a path with __ID__ where the application id goes.
export default class extends Controller {
    static targets = ["column", "card", "count"]
    static values = { moveUrlTemplate: String }

    connect() {
        this._dragging = null
        this._origin = null
    }

    // --- Drag lifecycle (wired via data-action on the card / column) ---

    onDragStart(e) {
        const card = e.target.closest("[data-application-id]")
        if (!card) return
        this._dragging = card
        this._origin = { column: card.closest("[data-stage-id]"), next: card.nextElementSibling }
        card.classList.add("is-dragging")
        e.dataTransfer.effectAllowed = "move"
        try { e.dataTransfer.setData("text/plain", card.dataset.applicationId) } catch (_) { /* ignore */ }
    }

    onDragEnd() {
        if (this._dragging) this._dragging.classList.remove("is-dragging")
        this.columnTargets.forEach((c) => c.classList.remove("is-drop-target"))
        this._dragging = null
        this._origin = null
    }

    onDragOver(e) {
        if (!this._dragging) return
        const column = e.target.closest("[data-stage-id]")
        if (!column) return
        e.preventDefault()
        e.dataTransfer.dropEffect = "move"
        this.columnTargets.forEach((c) => c.classList.toggle("is-drop-target", c === column))
    }

    onDrop(e) {
        if (!this._dragging) return
        const column = e.target.closest("[data-stage-id]")
        if (!column) return
        e.preventDefault()

        const card = this._dragging
        const fromColumn = this._origin.column
        const toStageId = column.dataset.stageId
        this.columnTargets.forEach((c) => c.classList.remove("is-drop-target"))

        // No-op drop back onto the same column.
        if (fromColumn === column) { this.onDragEnd(); return }

        // Optimistically move the card into the target column body.
        const body = column.querySelector("[data-board-column-body]") || column
        body.appendChild(card)
        this._recount()

        this._post(card.dataset.applicationId, toStageId).then((ok) => {
            if (!ok) {
                // Revert to the card's original position.
                const originBody = fromColumn.querySelector("[data-board-column-body]") || fromColumn
                if (this._origin.next && this._origin.next.parentNode === originBody) {
                    originBody.insertBefore(card, this._origin.next)
                } else {
                    originBody.appendChild(card)
                }
                this._recount()
                this._flash("Could not move the candidate — put it back.")
            }
            this._dragging = null
            this._origin = null
        })
    }

    _post(applicationId, stageId) {
        const url = this.moveUrlTemplateValue.replace("__ID__", encodeURIComponent(applicationId))
        const fd = new FormData()
        fd.set("pipeline_stage_id", stageId)
        return fetch(url, {
            method: "PATCH",
            headers: { "X-CSRF-Token": csrfToken(), Accept: "application/json" },
            body: fd
        })
            .then((r) => r.ok ? r.json() : { ok: false })
            .then((data) => Boolean(data && data.ok))
            .catch(() => false)
    }

    // Keep each column's header count in sync with its card total.
    _recount() {
        this.columnTargets.forEach((column) => {
            const count = column.querySelectorAll("[data-application-id]").length
            const label = column.querySelector("[data-board-column-count]")
            if (label) label.textContent = count
        })
    }

    _flash(message) {
        // Lightweight, dependency-free notice; the board is a focused surface.
        if (typeof window !== "undefined" && window.alert) window.alert(message)
    }
}
